<template>
  <div class="page-container reveal">
    <!-- ═══ LIST VIEW ═══ -->
    <template v-if="mode === 'list'">
      <el-card class="main-list-card">
        <div class="list-header">
          <div class="header-content">
            <h2 class="page-title">Gestion des Comptes</h2>
            <p class="page-subtitle">{{ users.length }} compte{{ users.length !== 1 ? 's' : '' }} référencés</p>
          </div>
          <div class="header-actions">
            <el-input
              v-model="searchQ"
              placeholder="Nom, téléphone..."
              class="w-64"
              @input="load"
            >
              <template #prefix><el-icon><Search /></el-icon></template>
            </el-input>

            <el-select v-model="roleFilter" class="w-40" @change="load">
              <el-option label="Tous les rôles" value="" />
              <el-option label="Admins" value="admin" />
              <el-option label="Support" value="support" />
              <el-option label="Clients" value="client" />
              <el-option label="Artisans" value="artisan" />
            </el-select>

            <el-button v-if="canCreateUsers" type="primary" @click="openCreate" :icon="Plus">Nouveau</el-button>
            <el-button @click="load" :loading="isLoading" circle :icon="Refresh" />
          </div>
        </div>

        <div v-if="selectedRows.length && (canUpdateUsers || canDeleteUsers)" class="bulk-bar">
          <span class="bulk-count">{{ selectedRows.length }} sélectionné(s)</span>
          <el-button v-if="canUpdateUsers" type="danger" size="small" :loading="bulkLoading" @click="bulkToggle(false)">Bloquer</el-button>
          <el-button v-if="canUpdateUsers" type="success" size="small" :loading="bulkLoading" @click="bulkToggle(true)">Débloquer</el-button>
          <el-button v-if="canDeleteUsers" type="danger" size="small" :loading="bulkLoading" @click="bulkDelete" plain>Supprimer</el-button>
        </div>

        <el-table :data="paged" v-loading="isLoading" class="custom-table" stripe @row-click="openDetail" @selection-change="onSelectionChange">
          <el-table-column type="selection" width="45" />
          <el-table-column label="Utilisateur" min-width="220" sortable :sort-method="(a, b) => (a.full_name || a.phone || '').localeCompare(b.full_name || b.phone || '')">
            <template #default="{ row }">
              <div class="flex items-center gap-3 cursor-pointer">
                <div class="w-10 h-10 rounded-xl bg-white/5 border border-profissa-glass-border flex items-center justify-center font-bold text-profissa-accent">
                  {{ (row.full_name || row.phone || 'U')[0].toUpperCase() }}
                </div>
                <div>
                  <div class="font-bold text-[0.95rem]">{{ row.full_name || '—' }}</div>
                  <div class="text-xs text-profissa-text-muted font-mono mt-0.5">{{ formatPhone(row.phone) }}</div>
                </div>
              </div>
            </template>
          </el-table-column>

          <el-table-column label="Rôle" width="120" sortable :sort-method="(a, b) => (a.role || '').localeCompare(b.role || '')">
            <template #default="{ row }">
              <el-tag :type="roleTagType(row.role)" class="uppercase tracking-wider font-bold">
                {{ roleLabel(row.role) }}
              </el-tag>
            </template>
          </el-table-column>

          <el-table-column label="Vérification" width="130">
            <template #default="{ row }">
              <el-tag :type="row.phone_verified ? 'success' : 'warning'" class="uppercase tracking-wider font-bold">
                <span class="flex items-center gap-1">
                  <el-icon v-if="row.phone_verified"><Check /></el-icon>
                  {{ row.phone_verified ? 'Vérifié' : 'En attente' }}
                </span>
              </el-tag>
            </template>
          </el-table-column>

          <el-table-column label="Statut" width="130" sortable :sort-method="(a, b) => Number(a.is_active) - Number(b.is_active)">
            <template #default="{ row }">
              <div class="px-3 py-1 rounded-full text-[0.7rem] font-bold uppercase tracking-wide inline-block"
                   :class="row.is_active ? 'bg-green-500/10 text-green-500 border border-green-500/20' : 'bg-red-500/10 text-red-500 border border-red-500/20'">
                {{ row.is_active ? 'Actif' : 'Bloqué' }}
              </div>
            </template>
          </el-table-column>

          <el-table-column label="Inscription" width="160" sortable :sort-method="(a, b) => new Date(a.created_at) - new Date(b.created_at)">
            <template #default="{ row }">
              <span class="text-profissa-text-muted text-sm">{{ fmtDate(row.created_at) }}</span>
            </template>
          </el-table-column>

          <el-table-column label="Actions" width="80" fixed="right" align="center">
            <template #default="{ row }">
              <ActionDropdown :actions="userActions(row)" @action="k => onAction(k, row)" />
            </template>
          </el-table-column>
        </el-table>

        <div class="flex justify-end pt-6 mt-4 border-t border-profissa-glass-border">
          <el-pagination
            v-if="users.length > pageSize"
            v-model:current-page="page"
            v-model:page-size="pageSize"
            layout="total, prev, pager, next"
            :total="users.length"
            @size-change="page = 1"
          />
        </div>
      </el-card>
    </template>

    <!-- ═══ DETAIL VIEW ═══ -->
    <template v-else-if="mode === 'detail'">
      <el-card v-loading="detailLoading" class="detail-card reveal">
        <div class="detail-header">
          <el-button @click="goBackToList" :icon="ArrowLeft" class="btn-back">Retour</el-button>
          <h2 class="detail-title">{{ detail?.full_name || detail?.phone || '—' }}</h2>
          <div class="detail-header-badges">
            <el-tag :type="roleTagType(detail?.role)" class="uppercase tracking-wider font-bold" size="large">
              {{ roleLabel(detail?.role) }}
            </el-tag>
            <div class="status-pill" :class="detail?.is_active ? 'active' : 'blocked'">
              {{ detail?.is_active ? 'Actif' : 'Bloqué' }}
            </div>
          </div>
        </div>

        <div v-if="detail" class="detail-body">
          <div class="detail-grid">
            <!-- Account Info -->
            <div class="info-group">
              <h4 class="group-title">Compte</h4>
              <div class="grid-2">
                <div class="detail-row"><span class="lbl">Téléphone</span><span class="val font-mono">{{ formatPhone(detail.phone) }}</span></div>
                <div class="detail-row"><span class="lbl">Nom complet</span><span class="val">{{ detail.full_name || '—' }}</span></div>
                <div class="detail-row">
                  <span class="lbl">Tél vérifié</span>
                  <span class="val"><span :class="detail.phone_verified ? 'badge-ok' : 'badge-warn'">{{ detail.phone_verified ? 'Oui' : 'Non' }}</span></span>
                </div>
                <div class="detail-row"><span class="lbl">Inscription</span><span class="val">{{ fmtDate(detail.created_at) }}</span></div>
              </div>
            </div>

            <div class="info-group">
              <h4 class="group-title">Contrôles Admin</h4>
              <div class="admin-controls-grid">
                <div class="detail-row">
                  <span class="lbl">Vérification téléphone</span>
                  <div class="val flex items-center gap-3 flex-wrap">
                    <span :class="verificationForm.phone_verified ? 'badge-ok' : 'badge-warn'">
                      {{ verificationForm.phone_verified ? 'Vérifié' : 'Non vérifié' }}
                    </span>
                    <el-button
                      v-if="canUpdateUsers"
                      size="small"
                      :loading="verificationLoading.phone"
                      @click="togglePhoneVerification"
                    >
                      {{ verificationForm.phone_verified ? 'Marquer non vérifié' : 'Marquer vérifié' }}
                    </el-button>
                  </div>
                </div>
                <template v-if="art">
                  <div class="detail-row admin-state-card">
                    <span class="lbl">État actuel du dossier</span>
                    <div class="admin-state-list">
                      <div class="detail-row">
                        <span class="lbl">Statut dossier</span>
                        <span class="val">
                          <span class="status-pill" :class="art.approval_status">{{ approvalLabel(art.approval_status) }}</span>
                        </span>
                      </div>
                      <div class="detail-row">
                        <span class="lbl">Étape actuelle</span>
                        <span class="val">{{ registrationStepLabel(art.registration_step) }}</span>
                      </div>
                      <div class="detail-row" v-if="art.sanction_state && art.sanction_state !== 'none'">
                        <span class="lbl">Sanction</span>
                        <span class="val badge-error">{{ sanctionStateLabel(art.sanction_state) }}</span>
                      </div>
                      <div class="detail-row" v-if="art.sanction_reason">
                        <span class="lbl">Motif</span>
                        <span class="val">{{ sanctionReasonLabel(art.sanction_reason) }}</span>
                      </div>
                    </div>
                  </div>
                  <div v-if="canUpdateUsers" class="detail-row admin-action-card">
                    <span class="lbl">Mettre à jour le statut dossier</span>
                    <div class="val flex items-center gap-3 flex-wrap">
                      <el-select v-model="verificationForm.approval_status" class="w-64">
                        <el-option label="Brouillon" value="draft" />
                        <el-option label="En attente" value="pending" />
                        <el-option label="À réviser" value="needs_revision" />
                        <el-option label="Approuvé" value="approved" />
                        <el-option label="Rejeté" value="rejected" />
                        <el-option label="Suspendu" value="suspended" />
                      </el-select>
                      <el-button
                        size="small"
                        type="primary"
                        :loading="verificationLoading.approval"
                        @click="saveArtisanVerification"
                      >Mettre à jour le statut</el-button>
                    </div>
                  </div>
                  <div v-if="canUpdateUsers" class="detail-row admin-action-card">
                    <span class="lbl">Mettre à jour l'étape inscription</span>
                    <div class="val flex items-center gap-3 flex-wrap">
                      <el-select v-model="verificationForm.registration_step" class="w-80">
                        <el-option
                          v-for="step in registrationStepOptions"
                          :key="step.value"
                          :label="step.label"
                          :value="step.value"
                        />
                      </el-select>
                      <el-button
                        size="small"
                        type="primary"
                        :loading="verificationLoading.step"
                        @click="saveArtisanRegistrationStep"
                      >Mettre à jour l'étape</el-button>
                    </div>
                  </div>
                </template>
              </div>
            </div>

            <!-- Artisan: Compétences -->
            <template v-if="art">
              <div class="info-group">
                <h4 class="group-title">Compétences</h4>
                <div class="grid-2">
                  <div class="detail-row"><span class="lbl">Spécialité</span><span class="val">{{ categoryName(art.category_id) }}</span></div>
                  <div class="detail-row"><span class="lbl">Expérience</span><span class="val">{{ art.experience_years }} an(s)</span></div>
                  <div class="detail-row"><span class="lbl">Motorisé</span><span class="val">{{ art.has_motorcycle ? 'Oui' : 'Non' }}</span></div>
                  <div class="detail-row"><span class="lbl">Diagnostic</span><span class="val">{{ art.diagnostic_price?.toLocaleString() }} FCFA</span></div>
                  <div class="detail-row"><span class="lbl">Note moyenne</span><span class="val">{{ art.rating_avg?.toFixed(1) || '—' }} ({{ art.rating_count }} avis)</span></div>
                  <div class="detail-row">
                    <span class="lbl">En ligne</span>
                    <span class="val"><span :class="art.is_online ? 'badge-ok' : 'badge-dim'">{{ art.is_online ? 'Oui' : 'Non' }}</span></span>
                  </div>
                </div>
              </div>

              <!-- Artisan: Approbation -->
              <div class="info-group">
                <h4 class="group-title">Approbation</h4>
                <div class="grid-2">
                  <div class="detail-row">
                    <span class="lbl">Statut dossier</span>
                    <span class="val"><span class="status-pill" :class="art.approval_status">{{ approvalLabel(art.approval_status) }}</span></span>
                  </div>
                  <div class="detail-row"><span class="lbl">Étape inscription</span><span class="val">{{ registrationStepLabel(art.registration_step) }}</span></div>
                  <div class="detail-row" v-if="art.sanction_state && art.sanction_state !== 'none'">
                    <span class="lbl">Sanction</span>
                    <span class="val badge-error">{{ sanctionStateLabel(art.sanction_state) }}</span>
                  </div>
                  <div class="detail-row" v-if="art.sanction_reason">
                    <span class="lbl">Motif</span>
                    <span class="val">{{ sanctionReasonLabel(art.sanction_reason) }}</span>
                  </div>
                </div>
              </div>

              <!-- Artisan: CNIB -->
              <div class="info-group">
                <h4 class="group-title">CNIB</h4>
                <div class="grid-2">
                  <div class="detail-row"><span class="lbl">Numéro</span><span class="val">{{ art.cnib_number || '—' }}</span></div>
                  <div class="detail-row"><span class="lbl">NIP</span><span class="val">{{ art.cnib_nip || '—' }}</span></div>
                  <div class="detail-row"><span class="lbl">Délivré le</span><span class="val">{{ art.cnib_issued_at || '—' }}</span></div>
                  <div class="detail-row"><span class="lbl">Expire le</span><span class="val">{{ art.cnib_expires_at || '—' }}</span></div>
                  <div class="detail-row">
                    <span class="lbl">Statut CNIB</span>
                    <span class="val"><span :class="art.cnib_is_expired ? 'badge-error' : 'badge-ok'">{{ art.cnib_is_expired ? 'Expirée' : 'Valide' }}</span></span>
                  </div>
                  <div class="detail-row">
                    <span class="lbl">OCR vérifié</span>
                    <span class="val"><span :class="art.cnib_ocr_verified ? 'badge-ok' : 'badge-warn'">{{ art.cnib_ocr_verified ? 'Oui' : 'Non' }}</span></span>
                  </div>
                </div>
              </div>

              <!-- Artisan: Témoin -->
              <div class="info-group">
                <h4 class="group-title">Témoin / Personne de confiance</h4>
                <div class="grid-2">
                  <div class="detail-row"><span class="lbl">Nom</span><span class="val">{{ art.witness_last_name || '—' }}</span></div>
                  <div class="detail-row"><span class="lbl">Prénom</span><span class="val">{{ art.witness_first_name || '—' }}</span></div>
                  <div class="detail-row"><span class="lbl">Téléphone</span><span class="val">{{ art.witness_phone || '—' }}</span></div>
                </div>
              </div>

              <!-- Artisan: Bio -->
              <div class="info-group span-2">
                <h4 class="group-title">Bio & Description</h4>
                <div class="bio-box">{{ art.bio || 'Aucune biographie fournie.' }}</div>
              </div>

              <!-- Artisan: Documents -->
              <div class="info-group span-2">
                <h4 class="group-title">Justificatifs & Documents</h4>
                <div class="doc-gallery">
                  <div v-for="doc in artisanDocEntries" :key="doc.key" class="doc-item">
                    <template v-if="doc.url && isPdf(doc.url)">
                      <a :href="doc.url" target="_blank" rel="noopener" class="doc-pdf-link">
                        <div class="doc-pdf-placeholder">
                          <el-icon :size="32"><Document /></el-icon>
                          <span>PDF</span>
                        </div>
                      </a>
                    </template>
                    <el-image
                      v-else-if="doc.url"
                      crossorigin="anonymous"
                      :src="doc.url"
                      :preview-src-list="artisanImageUrls"
                      :initial-index="artisanImageUrls.indexOf(doc.url)"
                      fit="cover"
                      class="doc-img"
                      :preview-teleported="true"
                      hide-on-click-modal
                    />
                    <div v-else class="doc-empty">
                      <el-icon :size="26"><UploadFilled /></el-icon>
                      <span>Aucun document</span>
                    </div>
                    <span class="doc-label">{{ doc.label }}</span>
                    <div v-if="canUpdateUsers" class="doc-actions">
                      <el-upload
                        :auto-upload="false"
                        :show-file-list="false"
                        :accept="doc.accept"
                        @change="(f) => replaceArtisanDoc(f, doc.key)"
                      >
                        <el-button size="small" :loading="docUploadingField === doc.key" plain>
                          {{ doc.url ? 'Remplacer' : 'Ajouter' }}
                        </el-button>
                      </el-upload>
                      <el-button
                        v-if="doc.url"
                        size="small"
                        text
                        type="danger"
                        :loading="docUploadingField === doc.key"
                        @click="clearArtisanDoc(doc.key)"
                      >Retirer</el-button>
                    </div>
                  </div>
                </div>
              </div>
            </template>

            <!-- Client: Minimal info (no artisan profile) -->
            <template v-if="detail.role === 'client' && !art">
              <div class="info-group">
                <h4 class="group-title">Profil Client</h4>
                <div class="grid-2">
                  <div class="detail-row"><span class="lbl">Rôle</span><span class="val">Client</span></div>
                  <div class="detail-row">
                    <span class="lbl">Statut</span>
                    <span class="val"><span :class="detail.is_active ? 'badge-ok' : 'badge-error'">{{ detail.is_active ? 'Actif' : 'Bloqué' }}</span></span>
                  </div>
                </div>
              </div>
            </template>

            <div v-if="detail.role === 'support' && canManageSupport" class="info-group span-2">
              <div class="notes-header">
                <h4 class="group-title !mb-0">Accès Support</h4>
                <span class="text-profissa-text-dim text-sm">{{ supportPermissionsSelection.length }} permission(s) active(s)</span>
              </div>

              <div v-if="canWriteUserNotes" class="notes-composer">
                <div v-if="supportPermissionsLoading" class="activity-loading">
                  <el-icon class="is-loading"><Refresh /></el-icon>
                  <span>Chargement des accès support...</span>
                </div>
                <template v-else>
                  <div class="support-permissions-summary">
                    <div class="detail-row">
                      <span class="lbl">Permissions par défaut</span>
                      <span class="val text-sm">{{ supportPermissionDefaults.join(', ') || 'Aucune' }}</span>
                    </div>
                  </div>
                  <el-checkbox-group v-model="supportPermissionsSelection" class="support-permissions-grid">
                    <el-checkbox
                      v-for="permission in supportPermissionCatalog"
                      :key="permission.code"
                      :label="permission.code"
                      class="support-permission-item"
                    >
                      <div class="support-permission-copy">
                        <div class="support-permission-label">{{ permission.label }}</div>
                        <div class="support-permission-code">{{ permission.code }}</div>
                      </div>
                    </el-checkbox>
                  </el-checkbox-group>
                  <div v-if="canWriteUserNotes" class="notes-actions">
                    <el-button
                      type="primary"
                      :loading="supportPermissionsSaving"
                      @click="saveSupportPermissions"
                    >Enregistrer les accès</el-button>
                  </div>
                </template>
              </div>
            </div>

            <div class="info-group span-2">
              <div class="notes-header">
                <h4 class="group-title !mb-0">Notes Internes</h4>
                <span class="text-profissa-text-dim text-sm">{{ notes.length }} note(s)</span>
              </div>

              <div class="notes-composer">
                <el-select v-model="noteDraft.note_type" class="w-40">
                  <el-option label="Ops" value="ops" />
                  <el-option label="Risque" value="risk" />
                  <el-option label="Support" value="support" />
                </el-select>
                <el-switch v-model="noteDraft.is_pinned" active-text="Épingler" />
                <el-input
                  v-model="noteDraft.body"
                  type="textarea"
                  :rows="3"
                  placeholder="Ajouter une note interne visible uniquement par les admins."
                />
                <div class="notes-actions">
                  <el-button
                    type="primary"
                    :loading="notesSaving"
                    :disabled="!noteDraft.body.trim()"
                    @click="createNote"
                  >Ajouter la note</el-button>
                </div>
              </div>

              <div v-if="notesLoading" class="activity-loading">
                <el-icon class="is-loading"><Refresh /></el-icon>
                <span>Chargement des notes...</span>
              </div>
              <div v-else-if="!notes.length" class="empty-state">Aucune note interne.</div>
              <div v-else class="notes-list">
                <div v-for="note in notes" :key="note.id" class="note-card" :class="{ pinned: note.is_pinned }">
                  <div class="note-meta">
                    <div class="flex items-center gap-2 flex-wrap">
                      <span class="note-type" :class="`note-type-${note.note_type}`">{{ noteTypeLabel(note.note_type) }}</span>
                      <span v-if="note.is_pinned" class="note-pin">Épinglée</span>
                    </div>
                    <span class="text-profissa-text-dim text-xs">
                      {{ note.author_name || note.author_phone || 'Admin' }} · {{ fmtDateTime(note.updated_at || note.created_at) }}
                    </span>
                  </div>

                  <el-input
                    v-if="editingNoteId === note.id"
                    v-model="editNoteDraft.body"
                    type="textarea"
                    :rows="3"
                  />
                  <div v-else class="note-body">{{ note.body }}</div>

                  <div class="notes-actions">
                    <template v-if="editingNoteId === note.id && canWriteUserNotes">
                      <el-button size="small" @click="cancelEditNote">Annuler</el-button>
                      <el-button
                        size="small"
                        type="primary"
                        :loading="noteActionId === note.id"
                        :disabled="!editNoteDraft.body.trim()"
                        @click="saveEditedNote(note)"
                      >Enregistrer</el-button>
                    </template>
                    <template v-else-if="canWriteUserNotes">
                      <el-button size="small" text @click="startEditNote(note)">Modifier</el-button>
                      <el-button size="small" text @click="togglePinned(note)">
                        {{ note.is_pinned ? 'Désépingler' : 'Épingler' }}
                      </el-button>
                      <el-button size="small" text type="danger" @click="removeNote(note)">Supprimer</el-button>
                    </template>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- ── Activity History ── -->
          <div class="activity-section">
            <h4 class="group-title span-2" style="margin-top: 1.5rem;">Historique d'activité</h4>

            <div v-if="activityLoading" class="activity-loading">
              <el-icon class="is-loading"><Refresh /></el-icon>
              <span>Chargement de l'historique...</span>
            </div>

            <el-tabs v-else-if="activity" v-model="activeTab" class="activity-tabs">
              <!-- Interventions -->
              <el-tab-pane :label="`Missions (${activity.interventions?.length || 0})`" name="interventions">
                <div v-if="!activity.interventions?.length" class="empty-state">Aucune mission enregistrée.</div>
                <el-table
                  v-else
                  :data="activity.interventions"
                  class="custom-table activity-table"
                  size="small"
                  max-height="320"
                  @row-click="openInterventionDetail"
                >
                  <el-table-column label="Rôle" width="90">
                    <template #default="{ row }">
                      <el-tag :type="row.role === 'artisan' ? 'info' : 'success'" size="small" class="uppercase font-bold">{{ row.role }}</el-tag>
                    </template>
                  </el-table-column>
                  <el-table-column label="Statut" width="150">
                    <template #default="{ row }">
                      <span class="status-pill" :class="row.status">{{ interventionLabel(row.status) }}</span>
                    </template>
                  </el-table-column>
                  <el-table-column label="Diagnostic" width="120">
                    <template #default="{ row }">{{ row.diagnostic_price?.toLocaleString() || '—' }} FCFA</template>
                  </el-table-column>
                  <el-table-column label="Prix final" width="120">
                    <template #default="{ row }">{{ row.final_price?.toLocaleString() || '—' }} FCFA</template>
                  </el-table-column>
                  <el-table-column label="Note" width="80">
                    <template #default="{ row }">{{ row.rating ?? '—' }}</template>
                  </el-table-column>
                  <el-table-column label="Date" width="160">
                    <template #default="{ row }"><span class="text-profissa-text-muted text-xs">{{ fmtDateTime(row.created_at) }}</span></template>
                  </el-table-column>
                </el-table>
              </el-tab-pane>

              <!-- Transactions -->
              <el-tab-pane :label="`Transactions (${activity.transactions?.length || 0})`" name="transactions">
                <div v-if="!activity.transactions?.length" class="empty-state">Aucune transaction enregistrée.</div>
                <el-table
                  v-else
                  :data="activity.transactions"
                  class="custom-table activity-table"
                  size="small"
                  max-height="320"
                  @row-click="openUserFinanceHistory"
                >
                  <el-table-column label="Type" width="130">
                    <template #default="{ row }">
                      <el-tag :type="txTagType(row.type)" size="small" class="uppercase font-bold">{{ row.type }}</el-tag>
                    </template>
                  </el-table-column>
                  <el-table-column label="Montant" width="140">
                    <template #default="{ row }">{{ row.amount?.toLocaleString() }} FCFA</template>
                  </el-table-column>
                  <el-table-column label="Description" min-width="200" prop="description" show-overflow-tooltip />
                  <el-table-column label="Référence" width="140" prop="reference" show-overflow-tooltip />
                  <el-table-column label="Date" width="160">
                    <template #default="{ row }"><span class="text-profissa-text-muted text-xs">{{ fmtDateTime(row.created_at) }}</span></template>
                  </el-table-column>
                </el-table>
              </el-tab-pane>

              <!-- Audit Logs -->
              <el-tab-pane :label="`Journal (${activity.audit_logs?.length || 0})`" name="audit">
                <div v-if="!activity.audit_logs?.length" class="empty-state">Aucun événement enregistré.</div>
                <el-table
                  v-else
                  :data="activity.audit_logs"
                  class="custom-table activity-table"
                  size="small"
                  max-height="320"
                  @row-click="openUserAuditHistory"
                >
                  <el-table-column label="Action" width="220">
                    <template #default="{ row }">
                      <span class="font-mono text-xs font-bold">{{ row.action }}</span>
                    </template>
                  </el-table-column>
                  <el-table-column label="Ressource" width="160">
                    <template #default="{ row }">
                      <span class="text-profissa-text-muted text-xs">{{ row.resource_type }}</span>
                    </template>
                  </el-table-column>
                  <el-table-column label="ID Ressource" width="140" show-overflow-tooltip>
                    <template #default="{ row }"><span class="font-mono text-xs">{{ row.resource_id || '—' }}</span></template>
                  </el-table-column>
                  <el-table-column label="IP" width="130">
                    <template #default="{ row }"><span class="font-mono text-xs text-profissa-text-muted">{{ row.ip_address || '—' }}</span></template>
                  </el-table-column>
                  <el-table-column label="Date" width="160">
                    <template #default="{ row }"><span class="text-profissa-text-muted text-xs">{{ fmtDateTime(row.created_at) }}</span></template>
                  </el-table-column>
                </el-table>
              </el-tab-pane>
            </el-tabs>

            <div v-else class="empty-state">Impossible de charger l'historique.</div>
          </div>
        </div>

        <div class="detail-footer">
          <div class="footer-actions-left">
            <el-button v-if="canUpdateUsers" type="primary" @click="openEdit(detail)" :icon="Edit">Modifier</el-button>
            <el-button v-if="canUpdateUsers" :type="detail?.is_active ? 'warning' : 'success'" :loading="actionLoading === detail?.id" @click="toggleActive(detail)" :icon="detail?.is_active ? CircleClose : Check">
              {{ detail?.is_active ? 'Désactiver' : 'Réactiver' }}
            </el-button>
            <el-button v-if="canDeleteUsers" type="danger" @click="onAction('delete', detail)" :icon="Delete">Supprimer</el-button>
          </div>
        </div>
      </el-card>
    </template>

    <!-- ═══ FORM VIEW (create / edit) ═══ -->
    <template v-else>
      <div class="flex flex-col gap-4 h-full">
        <div class="flex items-center justify-between flex-wrap gap-4 pb-4">
          <div class="flex items-center gap-4">
            <el-button @click="goBackToList" circle :icon="ArrowLeft" />
            <h2 class="text-2xl font-black">{{ isCreateMode ? 'Nouveau compte' : 'Modifier le compte' }}</h2>
          </div>
          <div class="flex gap-2">
            <el-button @click="goBackToList">Annuler</el-button>
            <el-button v-if="isCreateMode ? canCreateUsers : canUpdateUsers" type="primary" :disabled="!canSubmit" :loading="isSaving" @click="isCreateMode ? confirmCreate() : confirmEdit()" :icon="Check">
              {{ isCreateMode ? 'Créer le compte' : 'Enregistrer' }}
            </el-button>
          </div>
        </div>

        <el-card class="flex-1 overflow-y-auto max-w-4xl w-full mx-auto">
          <el-form :model="form" label-position="top" class="premium-form">
            <!-- Account info -->
            <div class="form-section-title">Informations d'identification</div>
            <div class="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-2">
              <el-form-item label="Numéro de téléphone *" required>
                <el-input v-model="form.phone" type="tel" placeholder="ex: 70 00 00 00" />
              </el-form-item>
              <el-form-item label="Nom et Prénom">
                <el-input v-model="form.full_name" placeholder="ex: Aminata Traoré" />
              </el-form-item>

              <el-form-item v-if="isCreateMode" label="Rôle de l'utilisateur *" required>
                <el-select v-model="form.role" class="w-full">
                  <el-option label="Client (Utilisateur standard)" value="client" />
                  <el-option label="Artisan (Prestataire)" value="artisan" />
                  <el-option label="Administrateur" value="admin" />
                  <el-option label="Support Opérations" value="support" />
                </el-select>
              </el-form-item>
              <el-form-item :label="isCreateMode ? 'Mot de passe d\'accès *' : 'Nouveau mot de passe'">
                <el-input v-model="form.new_password" type="password" :placeholder="isCreateMode ? '6 caractères minimum' : 'Laisser vide pour ne pas modifier'" show-password />
              </el-form-item>
            </div>

            <!-- Artisan profile -->
            <template v-if="form.role === 'artisan'">
              <div class="form-section-title mt-6">Profil Professionnel (Artisan)</div>
              <div class="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-2">
                <el-form-item label="Métier / Catégorie *" required>
                  <el-select v-model="form.category_id" class="w-full">
                    <el-option label="— Sélectionner un métier —" value="" />
                    <el-option v-for="c in categories" :key="c.id" :label="c.name || c.label_fr" :value="c.id" />
                  </el-select>
                </el-form-item>
                <el-form-item label="Statut d'approbation">
                  <el-select v-model="form.approval_status" class="w-full">
                    <el-option label="En attente de vérification" value="pending" />
                    <el-option label="Approuvé & En ligne" value="approved" />
                    <el-option label="Rejeté" value="rejected" />
                    <el-option label="Révision du dossier requise" value="needs_revision" />
                  </el-select>
                </el-form-item>
                <el-form-item label="Ville ou Quartier *" required>
                  <el-input v-model="form.quartier" placeholder="ex: Ouagadougou, Gounghin" />
                </el-form-item>
                <el-form-item label="Numéro WhatsApp *" required>
                  <el-input v-model="form.whatsapp" type="tel" placeholder="ex: 70 00 00 00" />
                </el-form-item>
                <el-form-item label="Années d'expérience">
                  <el-input-number v-model="form.experience_years" :min="0" :max="50" class="w-full" />
                </el-form-item>
                <el-form-item label="Tarif de base / Diagnostic (FCFA)">
                  <el-input-number v-model="form.diagnostic_price" :min="0" :step="500" class="w-full" />
                </el-form-item>
              </div>

              <el-form-item label="Présentation & Spécialités">
                <el-input v-model="form.bio" type="textarea" :rows="3" placeholder="Décrivez les compétences et spécialités de l'artisan..." />
              </el-form-item>
              <el-form-item>
                <el-switch v-model="form.has_motorcycle" active-text="Artisan Motorisé (Intervention rapide)" />
              </el-form-item>

              <!-- CNIB -->
              <div class="form-section-title mt-6">Vérification d'Identité (CNIB)</div>
              <div class="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-2">
                <el-form-item label="Numéro de pièce (CNIB)">
                  <el-input v-model="form.cnib_number" placeholder="ex: B12345678" />
                </el-form-item>
                <el-form-item label="Validation OCR du système">
                  <el-select v-model="form.cnib_ocr_verified" class="w-full">
                    <el-option label="Non analysé" :value="null" />
                    <el-option label="Valide (Concordant)" :value="true" />
                    <el-option label="Invalide (À vérifier)" :value="false" />
                  </el-select>
                </el-form-item>
              </div>

              <div class="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-4 mt-2">
                <!-- Recto -->
                <el-form-item label="CNIB Recto">
                  <div v-if="form.cnib_front_url" class="doc-preview">
                    <el-image :src="form.cnib_front_url" class="w-full h-32 object-cover rounded-xl border border-profissa-glass-border" :preview-src-list="[form.cnib_front_url]" hide-on-click-modal />
                    <el-button text type="danger" size="small" @click="form.cnib_front_url = ''" class="mt-2 w-full" :icon="Delete">Supprimer</el-button>
                  </div>
                  <el-upload v-else :auto-upload="false" :show-file-list="false" accept="image/*" @change="(f) => handleUpload(f, 'cnib_front_url')" class="w-full">
                    <div class="upload-dropzone">
                      <el-icon class="text-3xl text-profissa-text-muted mb-2"><UploadFilled /></el-icon>
                      <span class="text-sm font-bold text-profissa-accent">Uploader le recto</span>
                    </div>
                  </el-upload>
                </el-form-item>

                <!-- Verso -->
                <el-form-item label="CNIB Verso">
                  <div v-if="form.cnib_back_url" class="doc-preview">
                    <el-image :src="form.cnib_back_url" class="w-full h-32 object-cover rounded-xl border border-profissa-glass-border" :preview-src-list="[form.cnib_back_url]" hide-on-click-modal />
                    <el-button text type="danger" size="small" @click="form.cnib_back_url = ''" class="mt-2 w-full" :icon="Delete">Supprimer</el-button>
                  </div>
                  <el-upload v-else :auto-upload="false" :show-file-list="false" accept="image/*" @change="(f) => handleUpload(f, 'cnib_back_url')" class="w-full">
                    <div class="upload-dropzone">
                      <el-icon class="text-3xl text-profissa-text-muted mb-2"><UploadFilled /></el-icon>
                      <span class="text-sm font-bold text-profissa-accent">Uploader le verso</span>
                    </div>
                  </el-upload>
                </el-form-item>

                <!-- Selfie -->
                <el-form-item label="Selfie de sécurité">
                  <div v-if="form.selfie_url" class="doc-preview">
                    <el-image :src="form.selfie_url" class="w-full h-32 object-cover rounded-xl border border-profissa-glass-border" :preview-src-list="[form.selfie_url]" hide-on-click-modal />
                    <el-button text type="danger" size="small" @click="form.selfie_url = ''" class="mt-2 w-full" :icon="Delete">Supprimer</el-button>
                  </div>
                  <el-upload v-else :auto-upload="false" :show-file-list="false" accept="image/*" @change="(f) => handleUpload(f, 'selfie_url')" class="w-full">
                    <div class="upload-dropzone">
                      <el-icon class="text-3xl text-profissa-text-muted mb-2"><Avatar /></el-icon>
                      <span class="text-sm font-bold text-profissa-accent">Uploader le selfie</span>
                    </div>
                  </el-upload>
                </el-form-item>

                <!-- Casier -->
                <el-form-item label="Casier Judiciaire">
                  <div v-if="form.casier_url" class="doc-preview">
                    <template v-if="isPdf(form.casier_url)">
                      <a :href="form.casier_url" target="_blank" class="doc-pdf-link"><div class="doc-pdf-placeholder"><el-icon :size="32"><Document /></el-icon><span>PDF</span></div></a>
                    </template>
                    <el-image v-else :src="form.casier_url" class="w-full h-32 object-cover rounded-xl border border-profissa-glass-border" :preview-src-list="[form.casier_url]" hide-on-click-modal />
                    <el-button text type="danger" size="small" @click="form.casier_url = ''" class="mt-2 w-full" :icon="Delete">Supprimer</el-button>
                  </div>
                  <el-upload v-else :auto-upload="false" :show-file-list="false" accept="image/*,.pdf" @change="(f) => handleUpload(f, 'casier_url')" class="w-full">
                    <div class="upload-dropzone">
                      <el-icon class="text-3xl text-profissa-text-muted mb-2"><Document /></el-icon>
                      <span class="text-sm font-bold text-profissa-accent">Uploader le document</span>
                    </div>
                  </el-upload>
                </el-form-item>
              </div>

              <!-- Witness -->
              <div class="form-section-title mt-6">Contact d'Urgence / Témoin</div>
              <div class="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-2">
                <el-form-item label="Nom du garant"><el-input v-model="form.witness_last_name" placeholder="Traoré" /></el-form-item>
                <el-form-item label="Prénom du garant"><el-input v-model="form.witness_first_name" placeholder="Issa" /></el-form-item>
                <el-form-item label="Téléphone du garant">
                  <el-input v-model="form.witness_phone" type="tel" placeholder="ex: 70 00 00 00" />
                </el-form-item>
              </div>
            </template>

            <el-alert v-if="formError" :title="formError" type="error" show-icon :closable="false" class="mt-6" />
          </el-form>
        </el-card>
      </div>
    </template>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { ElMessage, ElMessageBox } from 'element-plus'
import { Search, Refresh, Plus, ArrowLeft, Check, UploadFilled, Avatar, Document, Delete, Edit, CircleClose } from '@element-plus/icons-vue'
import {
  listAllUsers, getUserDetail, getUserActivity, createUser, updateUser, deleteUser,
  listCategories, uploadFile as apiUploadFile, listUserAdminNotes, createUserAdminNote,
  updateUserAdminNote, deleteUserAdminNote, listPermissions, getUserPermissions, updateUserPermissions,
} from '../services/adminApi'
import { extractApiError, getAuthFileUrl } from '../api'
import ActionDropdown from './shared/ActionDropdown.vue'
import { useAdminPermissions } from '../composables/useAdminPermissions'
import { formatPhone } from '../utils/format'

const EMPTY_FORM = () => ({
  phone: '', full_name: '', role: 'client', new_password: '',
  category_id: '', quartier: '', whatsapp: '',
  experience_years: 0, has_motorcycle: false, diagnostic_price: 2000,
  bio: '', approval_status: 'pending',
  witness_first_name: '', witness_last_name: '', witness_phone: '',
  cnib_front_url: '', cnib_back_url: '', selfie_url: '', casier_url: '',
  cnib_number: '', cnib_ocr_verified: null,
})

const selectedRows = ref([])
const users        = ref([])
const categories   = ref([])
const isLoading    = ref(false)
const actionLoading= ref(null)
const docUploadingField = ref('')
const isSaving     = ref(false)
const formError    = ref('')
const searchQ      = ref('')
const roleFilter   = ref('')
const mode         = ref('list')     // 'list' | 'detail' | 'form'
const isCreateMode = ref(false)
const editTarget   = ref(null)
const form         = ref(EMPTY_FORM())
const page         = ref(1)
const pageSize     = ref(15)
const detail       = ref(null)
const detailLoading= ref(false)
const activity     = ref(null)
const activityLoading = ref(false)
const activeTab    = ref('interventions')
const notes        = ref([])
const notesLoading = ref(false)
const notesSaving  = ref(false)
const noteActionId = ref('')
const editingNoteId = ref('')
const noteDraft    = ref({ body: '', note_type: 'ops', is_pinned: false })
const editNoteDraft = ref({ body: '', note_type: 'ops', is_pinned: false })
const verificationForm = ref({ phone_verified: false, approval_status: 'pending', registration_step: 0 })
const verificationLoading = ref({ phone: false, approval: false, step: false })
const supportPermissionCatalog = ref([])
const supportPermissionsSelection = ref([])
const supportPermissionDefaults = ref([])
const supportPermissionsLoading = ref(false)
const supportPermissionsSaving = ref(false)
const registrationStepOptions = [
  { value: 0, label: '0 - Profil créé' },
  { value: 1, label: '1 - Informations personnelles renseignées' },
  { value: 2, label: '2 - Profil métier renseigné' },
  { value: 3, label: '3 - Documents d’identité transmis' },
  { value: 4, label: '4 - Dossier prêt pour validation' },
  { value: 5, label: '5 - Dossier validé et activable' },
]
const route        = useRoute()
const router       = useRouter()
const routeUserId  = computed(() => String(route.params.id || ''))
const { hasPermission } = useAdminPermissions()

const canCreateUsers = computed(() => hasPermission('users.create.any'))
const canUpdateUsers = computed(() => hasPermission('users.update.any'))
const canDeleteUsers = computed(() => hasPermission('users.delete.any'))
const canWriteUserNotes = computed(() => hasPermission('users.notes.write.any'))
const canManageSupport = computed(() => hasPermission('support.manage'))

const paged = computed(() => users.value.slice((page.value - 1) * pageSize.value, page.value * pageSize.value))
const onSelectionChange = (rows) => { selectedRows.value = rows }
const bulkLoading = ref(false)

const bulkToggle = async (activate) => {
  if (!canUpdateUsers.value) return
  const targets = selectedRows.value.filter(r => r.is_active !== activate)
  if (!targets.length) { ElMessage.warning('Aucun compte à modifier.'); return }
  try {
    await ElMessageBox.confirm(`${activate ? 'Débloquer' : 'Bloquer'} ${targets.length} compte(s) ?`, 'Action groupée', { type: 'warning' })
    bulkLoading.value = true
    let ok = 0
    for (const row of targets) { try { await updateUser(row.id, { is_active: activate }); ok++ } catch { /* skip */ } }
    ElMessage.success(`${ok} compte(s) ${activate ? 'débloqué(s)' : 'bloqué(s)'}.`)
    await load()
  } catch { /* cancelled */ }
  finally { bulkLoading.value = false }
}

const bulkDelete = async () => {
  if (!canDeleteUsers.value) return
  if (!selectedRows.value.length) return
  try {
    await ElMessageBox.confirm(`Supprimer définitivement ${selectedRows.value.length} compte(s) ?`, 'Suppression groupée', { type: 'error' })
    bulkLoading.value = true
    let ok = 0
    for (const row of selectedRows.value) { try { await deleteUser(row.id); ok++ } catch { /* skip */ } }
    ElMessage.success(`${ok} compte(s) supprimé(s).`)
    await load()
  } catch { /* cancelled */ }
  finally { bulkLoading.value = false }
}

const fmtDate      = (d) => d ? new Date(d).toLocaleDateString('fr-FR', { day:'numeric', month:'long', year:'numeric' }) : '—'
const roleTagType  = (r) => ({ admin:'danger', support:'warning', client:'success', artisan:'info' }[r] || '')
const roleLabel    = (r) => ({ admin:'Admin', support:'Support', client:'Client', artisan:'Artisan' }[r] || r)
const approvalLabel = (s) => ({
  draft: 'Brouillon', pending: 'En attente', needs_revision: 'À réviser',
  approved: 'Validé', rejected: 'Rejeté', suspended: 'Suspendu',
}[s] || s)
const registrationStepLabel = (step) => {
  const option = registrationStepOptions.find((entry) => entry.value === Number(step))
  return option ? option.label : (step ?? step === 0 ? `Étape ${step}` : '—')
}
const sanctionStateLabel = (state) => ({
  suspended: 'Compte suspendu',
  warning: 'Avertissement',
  restricted: 'Restrictions actives',
  none: 'Aucune',
}[state] || state)
const sanctionReasonLabel = (reason) => ({
  fraud_auto_suspension: 'Suspension automatique pour suspicion de fraude',
  fraud_manual_review: 'Contrôle manuel antifraude en cours',
  missing_documents: 'Documents manquants ou invalides',
  compliance_hold: 'Blocage de conformité',
}[reason] || String(reason || '—').replaceAll('_', ' '))
const isPdf = (url) => /\.pdf(\?|$)/i.test(url)
const fmtDateTime  = (d) => d ? new Date(d).toLocaleString('fr-FR', { day:'numeric', month:'short', year:'numeric', hour:'2-digit', minute:'2-digit' }) : '—'
const interventionLabel = (s) => ({
  proposed: 'Proposée', accepted: 'Acceptée', en_route: 'En route', arrived: 'Arrivé',
  quote_pending: 'Devis', working: 'En cours', pending_closure: 'Clôture', done: 'Terminée',
  cancelled: 'Annulée', disputed: 'Litige',
}[s] || s)
const txTagType = (t) => ({ earning: 'success', withdrawal: 'warning', reversal: 'danger' }[t] || '')
const noteTypeLabel = (t) => ({ ops: 'Ops', risk: 'Risque', support: 'Support' }[t] || t)

const categoryName = (id) => {
  if (!id) return '—'
  const cat = categories.value.find(c => c.id === id)
  return cat ? (cat.label_fr || cat.name) : id
}

const art = computed(() => detail.value?.artisan || null)

const docLabels = { photo_url: 'Photo Profil', cnib_front_url: 'CNIB Recto', cnib_back_url: 'CNIB Verso', selfie_url: 'Selfie', casier_url: 'Casier Judiciaire' }
const docAcceptMap = {
  photo_url: 'image/*',
  cnib_front_url: 'image/*',
  cnib_back_url: 'image/*',
  selfie_url: 'image/*',
  casier_url: 'image/*,.pdf',
}
const artisanDocEntries = ref([])
watch(art, async (artVal) => {
  const keys = ['photo_url', 'cnib_front_url', 'cnib_back_url', 'selfie_url', 'casier_url']
  artisanDocEntries.value = await Promise.all(keys.map(async (key) => ({
    key,
    label: docLabels[key],
    accept: docAcceptMap[key],
    url: artVal?.[key] ? await getAuthFileUrl(artVal[key]) : '',
  })))
}, { immediate: true })
const artisanImageUrls = computed(() => artisanDocEntries.value.map(d => d.url).filter(u => u && !isPdf(u)))

const hydrateSupportPermissions = async (userId) => {
  supportPermissionsLoading.value = true
  try {
    if (!supportPermissionCatalog.value.length) {
      supportPermissionCatalog.value = await listPermissions()
    }
    const payload = await getUserPermissions(userId)
    supportPermissionDefaults.value = payload.default_permissions || []
    supportPermissionsSelection.value = payload.effective_permissions || []
  } catch (e) {
    ElMessage.error(extractApiError(e))
  } finally {
    supportPermissionsLoading.value = false
  }
}

const userActions = (row) => {
  const actions = [{ key: 'detail', label: 'Voir le dossier', icon: 'View' }]
  if (canUpdateUsers.value) {
    actions.push({ key: 'edit', label: 'Modifier', icon: 'Edit' })
    actions.push({ key: 'toggle', label: row.is_active ? 'Désactiver' : 'Réactiver', icon: row.is_active ? 'CircleClose' : 'Check' })
  }
  if (canDeleteUsers.value) {
    actions.push({ key: 'delete', label: 'Supprimer', icon: 'Delete', danger: true, divided: true })
  }
  return actions
}

const onAction = async (key, row) => {
  if (key === 'detail') return openDetail(row)
  if (key === 'edit' && !canUpdateUsers.value) return
  if (key === 'toggle' && !canUpdateUsers.value) return
  if (key === 'delete' && !canDeleteUsers.value) return
  if (key === 'edit') return openEdit(row)
  if (key === 'toggle') return toggleActive(row)
  if (key === 'delete') {
    try {
      await ElMessageBox.confirm('Supprimer définitivement ce compte ? Toute donnée liée sera perdue.', 'Zone de Danger', {
        confirmButtonText: 'Supprimer', cancelButtonText: 'Annuler', type: 'error',
      })
      await confirmDelete(row)
    } catch { /* cancelled */ }
  }
}

const canSubmit = computed(() => {
  if (!form.value.phone) return false
  if (isCreateMode.value && (!form.value.new_password || form.value.new_password.length < 6)) return false
  if (form.value.role === 'artisan') {
    if (!form.value.category_id || !form.value.quartier || !form.value.whatsapp) return false
  }
  return true
})

let debounceTimer = null
const load = () => {
  clearTimeout(debounceTimer)
  debounceTimer = setTimeout(async () => {
    isLoading.value = true
    try { users.value = await listAllUsers(searchQ.value, roleFilter.value); page.value = 1 }
    catch (e) { ElMessage.error(extractApiError(e)) }
    finally { isLoading.value = false }
  }, 300)
}

const loadCategories = async () => {
  try { categories.value = await listCategories() }
  catch { /* non-blocking */ }
}

const openDetail = async (row) => {
  if (!routeUserId.value && row?.id) {
    await router.push(`/users/${row.id}`)
    return
  }
  detail.value = row
  detailLoading.value = true
  activityLoading.value = true
  notesLoading.value = true
  activity.value = null
  notes.value = []
  activeTab.value = 'interventions'
  mode.value = 'detail'
  try {
    const [d, act, noteResult] = await Promise.allSettled([
      getUserDetail(row.id),
      getUserActivity(row.id),
      listUserAdminNotes(row.id),
    ])
    if (d.status === 'fulfilled') detail.value = d.value
    else ElMessage.error(extractApiError(d.reason))
    if (act.status === 'fulfilled') activity.value = act.value
    if (noteResult.status === 'fulfilled') notes.value = noteResult.value
    else notes.value = []
    if (detail.value) {
      verificationForm.value = {
        phone_verified: !!detail.value.phone_verified,
        approval_status: detail.value.artisan?.approval_status || 'pending',
        registration_step: detail.value.artisan?.registration_step ?? 0,
      }
    } else {
      verificationForm.value = { phone_verified: false, approval_status: 'pending', registration_step: 0 }
    }
    if (detail.value?.role === 'support') {
      await hydrateSupportPermissions(detail.value.id)
    } else {
      supportPermissionsSelection.value = []
      supportPermissionDefaults.value = []
    }
  } catch (e) {
    ElMessage.error(extractApiError(e))
  } finally {
    detailLoading.value = false
    activityLoading.value = false
    notesLoading.value = false
  }
}

const handleUpload = async (uploadedFile, field) => {
  const file = uploadedFile.raw || uploadedFile
  if (!file) return
  try {
    ElMessage.info('Transfert sécurisé en cours...')
    const result = await apiUploadFile(file)
    form.value[field] = result.url
    ElMessage.success('Document validé et enregistré.')
  } catch (e) {
    ElMessage.error(extractApiError(e))
  }
}

const replaceArtisanDoc = async (uploadedFile, field) => {
  if (!canUpdateUsers.value) return
  if (!detail.value?.id) return
  const file = uploadedFile.raw || uploadedFile
  if (!file) return
  docUploadingField.value = field
  try {
    const result = await apiUploadFile(file)
    await updateUser(detail.value.id, { [field]: result.url })
    ElMessage.success('Document mis à jour.')
    await openDetail(detail.value)
    await load()
  } catch (e) {
    ElMessage.error(extractApiError(e))
  } finally {
    docUploadingField.value = ''
  }
}

const clearArtisanDoc = async (field) => {
  if (!canUpdateUsers.value) return
  if (!detail.value?.id) return
  docUploadingField.value = field
  try {
    await updateUser(detail.value.id, { [field]: '' })
    ElMessage.success('Document retiré.')
    await openDetail(detail.value)
    await load()
  } catch (e) {
    ElMessage.error(extractApiError(e))
  } finally {
    docUploadingField.value = ''
  }
}

const reloadNotes = async () => {
  if (!detail.value?.id) return
  notesLoading.value = true
  try {
    notes.value = await listUserAdminNotes(detail.value.id)
  } catch (e) {
    ElMessage.error(extractApiError(e))
  } finally {
    notesLoading.value = false
  }
}

const createNote = async () => {
  if (!canWriteUserNotes.value) return
  if (!detail.value?.id || !noteDraft.value.body.trim()) return
  notesSaving.value = true
  try {
    await createUserAdminNote(detail.value.id, {
      body: noteDraft.value.body.trim(),
      note_type: noteDraft.value.note_type,
      is_pinned: noteDraft.value.is_pinned,
    })
    noteDraft.value = { body: '', note_type: 'ops', is_pinned: false }
    await reloadNotes()
    ElMessage.success('Note ajoutée.')
  } catch (e) {
    ElMessage.error(extractApiError(e))
  } finally {
    notesSaving.value = false
  }
}

const startEditNote = (note) => {
  if (!canWriteUserNotes.value) return
  editingNoteId.value = note.id
  editNoteDraft.value = {
    body: note.body,
    note_type: note.note_type,
    is_pinned: note.is_pinned,
  }
}

const cancelEditNote = () => {
  editingNoteId.value = ''
  editNoteDraft.value = { body: '', note_type: 'ops', is_pinned: false }
}

const saveEditedNote = async (note) => {
  if (!canWriteUserNotes.value) return
  if (!detail.value?.id || !editNoteDraft.value.body.trim()) return
  noteActionId.value = note.id
  try {
    await updateUserAdminNote(detail.value.id, note.id, {
      body: editNoteDraft.value.body.trim(),
      note_type: note.note_type,
      is_pinned: note.is_pinned,
    })
    cancelEditNote()
    await reloadNotes()
    ElMessage.success('Note mise à jour.')
  } catch (e) {
    ElMessage.error(extractApiError(e))
  } finally {
    noteActionId.value = ''
  }
}

const togglePinned = async (note) => {
  if (!canWriteUserNotes.value) return
  if (!detail.value?.id) return
  noteActionId.value = note.id
  try {
    await updateUserAdminNote(detail.value.id, note.id, { is_pinned: !note.is_pinned })
    await reloadNotes()
  } catch (e) {
    ElMessage.error(extractApiError(e))
  } finally {
    noteActionId.value = ''
  }
}

const removeNote = async (note) => {
  if (!canWriteUserNotes.value) return
  if (!detail.value?.id) return
  try {
    await ElMessageBox.confirm('Supprimer cette note interne ?', 'Confirmation', { type: 'warning' })
    noteActionId.value = note.id
    await deleteUserAdminNote(detail.value.id, note.id)
    await reloadNotes()
    ElMessage.success('Note supprimée.')
  } catch (e) {
    if (e !== 'cancel' && e !== 'close') {
      ElMessage.error(extractApiError(e))
    }
  } finally {
    noteActionId.value = ''
  }
}

const openInterventionDetail = (row) => {
  if (!row?.id) return
  router.push(`/missions/${row.id}`)
}

const openUserFinanceHistory = () => {
  const artisanId = detail.value?.artisan?.id
  if (!artisanId) return
  router.push({
    path: '/finance',
    query: {
      tab: 'artisan',
      artisanId,
      artisanName: detail.value?.full_name || '',
      artisanPhone: detail.value?.phone || '',
    },
  })
}

const openUserAuditHistory = () => {
  if (!detail.value?.id) return
  router.push({
    path: '/audit',
    query: {
      userId: detail.value.id,
      userLabel: detail.value?.full_name || detail.value?.phone || '',
    },
  })
}

const togglePhoneVerification = async () => {
  if (!canUpdateUsers.value) return
  if (!detail.value?.id) return
  verificationLoading.value.phone = true
  try {
    await updateUser(detail.value.id, { phone_verified: !verificationForm.value.phone_verified })
    await openDetail({ id: detail.value.id })
    ElMessage.success('Statut téléphone mis à jour.')
  } catch (e) {
    ElMessage.error(extractApiError(e))
  } finally {
    verificationLoading.value.phone = false
  }
}

const saveArtisanVerification = async () => {
  if (!canUpdateUsers.value) return
  if (!detail.value?.id || !art.value) return
  verificationLoading.value.approval = true
  try {
    await updateUser(detail.value.id, { approval_status: verificationForm.value.approval_status })
    await openDetail({ id: detail.value.id })
    ElMessage.success('Statut dossier mis à jour.')
  } catch (e) {
    ElMessage.error(extractApiError(e))
  } finally {
    verificationLoading.value.approval = false
  }
}

const saveArtisanRegistrationStep = async () => {
  if (!canUpdateUsers.value) return
  if (!detail.value?.id || !art.value) return
  verificationLoading.value.step = true
  try {
    await updateUser(detail.value.id, { registration_step: verificationForm.value.registration_step })
    await openDetail({ id: detail.value.id })
    ElMessage.success('Étape inscription mise à jour.')
  } catch (e) {
    ElMessage.error(extractApiError(e))
  } finally {
    verificationLoading.value.step = false
  }
}

const saveSupportPermissions = async () => {
  if (!canManageSupport.value) return
  if (!detail.value?.id || detail.value.role !== 'support') return
  supportPermissionsSaving.value = true
  try {
    const payload = await updateUserPermissions(detail.value.id, supportPermissionsSelection.value)
    supportPermissionDefaults.value = payload.default_permissions || []
    supportPermissionsSelection.value = payload.effective_permissions || []
    ElMessage.success('Accès support mis à jour.')
  } catch (e) {
    ElMessage.error(extractApiError(e))
  } finally {
    supportPermissionsSaving.value = false
  }
}

const openCreate = () => {
  if (!canCreateUsers.value) return
  form.value = EMPTY_FORM(); formError.value = ''
  isCreateMode.value = true; mode.value = 'form'
}

const openEdit = async (u) => {
  if (!canUpdateUsers.value) return
  const target = u || detail.value
  editTarget.value = target
  form.value = EMPTY_FORM()
  form.value.phone = target.phone
  form.value.full_name = target.full_name || ''
  form.value.role = target.role
  formError.value = ''; isCreateMode.value = false; mode.value = 'form'

  if (target.role === 'artisan') {
    try {
      const d = target.artisan ? target : await getUserDetail(target.id)
      if (d.artisan) {
        const a = d.artisan
        Object.assign(form.value, {
          category_id: a.category_id || '', quartier: a.quartier || '',
          whatsapp: a.whatsapp || '', experience_years: a.experience_years ?? 0,
          has_motorcycle: a.has_motorcycle ?? false, diagnostic_price: a.diagnostic_price ?? 2000,
          bio: a.bio || '', approval_status: a.approval_status || 'pending',
          witness_first_name: a.witness_first_name || '', witness_last_name: a.witness_last_name || '',
          witness_phone: a.witness_phone || '', cnib_front_url: a.cnib_front_url || '',
          cnib_back_url: a.cnib_back_url || '', selfie_url: a.selfie_url || '',
          casier_url: a.casier_url || '', cnib_number: a.cnib_number || '',
          cnib_ocr_verified: a.cnib_ocr_verified ?? null,
        })
      }
    } catch { /* ignore */ }
  }
}

const buildPayload = () => {
  const p = { phone: form.value.phone, full_name: form.value.full_name }
  if (form.value.new_password) p.new_password = form.value.new_password
  if (isCreateMode.value) { p.role = form.value.role; p.password = form.value.new_password; delete p.new_password }
  if (form.value.role === 'artisan') {
    Object.assign(p, {
      category_id: form.value.category_id || null, quartier: form.value.quartier,
      whatsapp: form.value.whatsapp, experience_years: form.value.experience_years,
      has_motorcycle: form.value.has_motorcycle, diagnostic_price: form.value.diagnostic_price,
      bio: form.value.bio || null, approval_status: form.value.approval_status,
      witness_first_name: form.value.witness_first_name || null, witness_last_name: form.value.witness_last_name || null,
      witness_phone: form.value.witness_phone || null, cnib_front_url: form.value.cnib_front_url || null,
      cnib_back_url: form.value.cnib_back_url || null, selfie_url: form.value.selfie_url || null,
      casier_url: form.value.casier_url || null, cnib_number: form.value.cnib_number || null,
      cnib_ocr_verified: form.value.cnib_ocr_verified,
    })
  }
  return p
}

const confirmCreate = async () => {
  if (!canCreateUsers.value) return
  isSaving.value = true; formError.value = ''
  try {
    await createUser(buildPayload())
    ElMessage.success('Identité numérique générée.')
    goBackToList(); await load()
  } catch (e) { formError.value = extractApiError(e) }
  finally { isSaving.value = false }
}

const confirmEdit = async () => {
  if (!canUpdateUsers.value) return
  isSaving.value = true; formError.value = ''
  try {
    await updateUser(editTarget.value.id, buildPayload())
    ElMessage.success('Profil mis à jour avec succès.')
    goBackToList(); await load()
  } catch (e) { formError.value = extractApiError(e) }
  finally { isSaving.value = false }
}

const toggleActive = async (u) => {
  if (!canUpdateUsers.value) return
  const target = u || detail.value
  actionLoading.value = target.id
  try {
    await updateUser(target.id, { is_active: !target.is_active })
    ElMessage.success(target.is_active ? 'Accès restreint.' : 'Accès rétabli.')
    if (mode.value === 'detail') await openDetail(target)
    await load()
  } catch (e) { ElMessage.error(extractApiError(e)) }
  finally { actionLoading.value = null }
}

const confirmDelete = async (u) => {
  if (!canDeleteUsers.value) return
  const target = u || detail.value
  actionLoading.value = target.id
  try {
    await deleteUser(target.id)
    ElMessage.success('Entité supprimée de la base de données.')
    goBackToList(); await load()
  } catch (e) { ElMessage.error(extractApiError(e)) }
  finally { actionLoading.value = null }
}

const resetListState = () => {
  mode.value = 'list'
  editTarget.value = null
  detail.value = null
  activity.value = null
  notes.value = []
  supportPermissionsSelection.value = []
  supportPermissionDefaults.value = []
  formError.value = ''
  editingNoteId.value = ''
  noteDraft.value = { body: '', note_type: 'ops', is_pinned: false }
}

const goBackToList = () => {
  if (routeUserId.value) {
    router.push('/users')
    return
  }
  resetListState()
}

onMounted(async () => {
  load()
  loadCategories()
  if (routeUserId.value) {
    await openDetail({ id: routeUserId.value })
  }
})
</script>

<style scoped>
.page-container { @apply h-full flex flex-col; }
.main-list-card { @apply flex-1 flex flex-col; }
.list-header { @apply flex justify-between items-end pb-8; }
.page-title { @apply text-2xl font-black mb-1; }
.page-subtitle { @apply text-profissa-text-muted text-sm font-semibold; }
.header-actions { @apply flex gap-3 items-center; }
.bulk-bar { @apply flex items-center gap-3 px-4 py-3 mb-4 rounded-xl; background: rgba(255, 107, 0, 0.06); border: 1px solid rgba(255, 107, 0, 0.15); }
.bulk-count { @apply font-extrabold text-sm text-profissa-accent mr-auto; }

/* Detail View */
.detail-card { flex: 1; display: flex; flex-direction: column; overflow: hidden; }
.detail-header { display: flex; align-items: center; gap: 1rem; padding-bottom: 1.5rem; border-bottom: 1px solid var(--glass-border); margin-bottom: 1.5rem; flex-wrap: wrap; }
.btn-back { font-weight: 700; border-radius: 8px; }
.detail-title { font-size: 1.5rem; font-weight: 800; margin: 0; flex: 1; min-width: 200px; }
.detail-header-badges { display: flex; gap: 0.75rem; align-items: center; }
.detail-body { flex: 1; overflow-y: auto; padding-right: 10px; }
.detail-footer { display: flex; justify-content: space-between; align-items: center; padding-top: 1.5rem; border-top: 1px solid var(--glass-border); margin-top: 1.5rem; }
.footer-actions-left { display: flex; gap: 0.75rem; flex-wrap: wrap; }

.detail-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 2rem; padding: 1rem 0; }
.info-group { display: flex; flex-direction: column; gap: 1rem; }
.info-group.span-2 { grid-column: span 2; }
.group-title { font-family: 'Outfit', sans-serif; font-size: 0.9rem; text-transform: uppercase; letter-spacing: 1px; color: var(--accent); font-weight: 800; margin-bottom: 0.5rem; }
.grid-2 { display: grid; grid-template-columns: 1fr 1fr; gap: 1rem; }
.admin-controls-grid { display: grid; grid-template-columns: 1fr; gap: 1rem; }
.admin-state-card,
.admin-action-card { padding: 1rem; border: 1px solid var(--glass-border); border-radius: 14px; background: var(--glass-bg); }
.admin-state-list { display: grid; gap: 0.85rem; }

.detail-row { display: flex; flex-direction: column; gap: 0.25rem; }
.detail-row .lbl { font-size: 0.7rem; color: var(--text-dim); font-weight: 700; text-transform: uppercase; }
.detail-row .val { font-size: 1rem; font-weight: 600; }

.bio-box { background: var(--glass-bg); border: 1px solid var(--glass-border); border-radius: 12px; padding: 1.25rem; line-height: 1.6; color: var(--text-muted); font-size: 0.95rem; }

.doc-gallery { display: grid; grid-template-columns: repeat(auto-fill, minmax(140px, 1fr)); gap: 1rem; }
.doc-item { display: flex; flex-direction: column; gap: 0.5rem; cursor: pointer; }
.doc-img { width: 100%; height: 100px; border-radius: 10px; border: 1px solid var(--glass-border); }
.doc-empty { width: 100%; height: 100px; border-radius: 10px; border: 1px dashed var(--glass-border); background: var(--glass-bg); display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 6px; color: var(--text-dim); }
.doc-label { font-size: 0.7rem; font-weight: 700; color: var(--text-dim); text-align: center; }
.doc-actions { display: flex; justify-content: center; gap: 0.5rem; flex-wrap: wrap; }

.doc-pdf-link { text-decoration: none; display: block; }
.doc-pdf-placeholder { width: 100%; height: 100px; border-radius: 10px; border: 1px solid var(--glass-border); background: var(--glass-bg); display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 4px; color: var(--text-muted); transition: background 0.2s; }
.doc-pdf-placeholder:hover { background: var(--surface-hover); }
.doc-pdf-placeholder span { font-size: 0.75rem; font-weight: 700; }

.status-pill { font-size: 0.75rem; font-weight: 800; text-transform: uppercase; letter-spacing: 0.5px; padding: 4px 12px; border-radius: 20px; display: inline-block; }
.status-pill.active { background: rgba(34, 197, 94, 0.1); color: var(--success); border: 1px solid rgba(34, 197, 94, 0.2); }
.status-pill.blocked { background: rgba(239, 68, 68, 0.1); color: var(--error); border: 1px solid rgba(239, 68, 68, 0.2); }
.status-pill.draft { background: rgba(148, 163, 184, 0.1); color: #94a3b8; border: 1px solid rgba(148, 163, 184, 0.2); }
.status-pill.pending { background: rgba(245, 158, 11, 0.1); color: var(--warning); border: 1px solid rgba(245, 158, 11, 0.2); }
.status-pill.approved { background: rgba(34, 197, 94, 0.1); color: var(--success); border: 1px solid rgba(34, 197, 94, 0.2); }
.status-pill.rejected { background: rgba(239, 68, 68, 0.1); color: var(--error); border: 1px solid rgba(239, 68, 68, 0.2); }
.status-pill.needs_revision { background: rgba(245, 158, 11, 0.1); color: var(--warning); border: 1px solid rgba(245, 158, 11, 0.2); }

.badge-ok { color: var(--success); font-weight: 700; }
.badge-warn { color: var(--warning); font-weight: 700; }
.badge-error { color: var(--error); font-weight: 700; }
.badge-dim { color: var(--text-dim); font-weight: 700; }

/* Activity section */
.activity-section { border-top: 1px solid var(--glass-border); padding-top: 1.5rem; margin-top: 1rem; }
.activity-loading { display: flex; align-items: center; gap: 0.5rem; color: var(--text-muted); padding: 2rem 0; justify-content: center; }
.empty-state { color: var(--text-dim); font-size: 0.9rem; padding: 2rem 0; text-align: center; }
.activity-table :deep(.el-table__row) { cursor: pointer; }
.notes-header { display: flex; align-items: center; justify-content: space-between; gap: 1rem; margin-bottom: 1rem; }
.notes-composer { display: flex; flex-direction: column; gap: 0.75rem; padding: 1rem; border: 1px solid var(--glass-border); border-radius: 14px; background: var(--glass-bg); margin-bottom: 1rem; }
.support-permissions-summary { padding: 0.85rem 1rem; border-radius: 12px; background: var(--glass-bg); border: 1px solid var(--glass-border); }
.support-permissions-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(240px, 1fr)); gap: 0.75rem; }
.support-permission-item { margin-right: 0; padding: 0.9rem; border-radius: 12px; border: 1px solid var(--glass-border); background: var(--glass-bg); align-items: flex-start; }
.support-permission-copy { display: flex; flex-direction: column; gap: 0.2rem; }
.support-permission-label { font-weight: 700; color: var(--text-main); }
.support-permission-code { font-size: 0.72rem; color: var(--text-dim); font-family: ui-monospace, SFMono-Regular, Menlo, monospace; }
.notes-list { display: flex; flex-direction: column; gap: 0.75rem; }
.note-card { border: 1px solid var(--glass-border); border-radius: 14px; padding: 1rem; background: var(--glass-bg); display: flex; flex-direction: column; gap: 0.75rem; }
.note-card.pinned { border-color: rgba(255, 107, 0, 0.35); background: rgba(255, 107, 0, 0.06); }
.note-meta { display: flex; justify-content: space-between; gap: 1rem; flex-wrap: wrap; align-items: center; }
.note-type { font-size: 0.72rem; font-weight: 800; text-transform: uppercase; letter-spacing: 0.06em; padding: 4px 10px; border-radius: 999px; }
.note-type-ops { color: #60a5fa; background: rgba(96,165,250,0.12); }
.note-type-risk { color: #f87171; background: rgba(248,113,113,0.12); }
.note-type-support { color: #34d399; background: rgba(52,211,153,0.12); }
.note-pin { font-size: 0.72rem; font-weight: 700; color: var(--accent); }
.note-body { white-space: pre-wrap; line-height: 1.55; color: var(--text-main); }
.notes-actions { display: flex; gap: 0.5rem; flex-wrap: wrap; justify-content: flex-end; }

/* Intervention status pills in tables */
.status-pill.proposed { background: rgba(148,163,184,0.1); color: #94a3b8; border: 1px solid rgba(148,163,184,0.2); }
.status-pill.accepted { background: rgba(59,130,246,0.1); color: #3b82f6; border: 1px solid rgba(59,130,246,0.2); }
.status-pill.en_route { background: rgba(139,92,246,0.1); color: #8b5cf6; border: 1px solid rgba(139,92,246,0.2); }
.status-pill.arrived { background: rgba(14,165,233,0.1); color: #0ea5e9; border: 1px solid rgba(14,165,233,0.2); }
.status-pill.quote_pending { background: rgba(245,158,11,0.1); color: var(--warning); border: 1px solid rgba(245,158,11,0.2); }
.status-pill.working { background: rgba(249,115,22,0.1); color: #f97316; border: 1px solid rgba(249,115,22,0.2); }
.status-pill.pending_closure { background: rgba(168,85,247,0.1); color: #a855f7; border: 1px solid rgba(168,85,247,0.2); }
.status-pill.done { background: rgba(34,197,94,0.1); color: var(--success); border: 1px solid rgba(34,197,94,0.2); }
.status-pill.cancelled { background: rgba(239,68,68,0.1); color: var(--error); border: 1px solid rgba(239,68,68,0.2); }
.status-pill.disputed { background: rgba(239,68,68,0.1); color: var(--error); border: 1px solid rgba(239,68,68,0.2); }

/* Form View */
.form-section-title {
  @apply text-xs font-black uppercase tracking-widest text-profissa-accent border-b border-profissa-glass-border pb-2 mb-4;
}

.upload-dropzone {
  @apply w-full h-32 border-2 border-dashed border-profissa-glass-border rounded-xl flex flex-col items-center justify-center cursor-pointer transition-all bg-white/5;
}
.upload-dropzone:hover {
  @apply border-profissa-accent;
  background-color: var(--accent-soft);
}

:deep(.premium-form .el-form-item__label) {
  @apply text-[0.75rem] font-bold text-profissa-text-muted uppercase tracking-wide pb-1;
}
</style>

<style>
/* el-image viewer overrides */
.el-image-viewer__close { font-size: 28px !important; top: 20px !important; right: 20px !important; width: 48px !important; height: 48px !important; background: rgba(0,0,0,0.6) !important; border-radius: 50% !important; color: #fff !important; }
</style>
